import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Search, ChevronLeft, ChevronRight, Users, MoreVertical, 
  Trash2, Edit, Eye, Target, Calendar, Download, FileSpreadsheet
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminGroups = () => {
  const { token } = useOutletContext();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  const [groupDetail, setGroupDetail] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [editForm, setEditForm] = useState({});
  const limit = 20;

  useEffect(() => {
    fetchGroups();
  }, [page, search, statusFilter]);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        token,
        skip: page * limit,
        limit
      });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/admin/groups?${params}`);
      setGroups(response.data.groups);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDetail = async (groupId) => {
    try {
      const response = await axios.get(`${API}/admin/groups/${groupId}?token=${token}`);
      setGroupDetail(response.data);
      setShowDetail(true);
    } catch (error) {
      toast.error('Failed to fetch group details');
    }
  };

  const handleEdit = (group) => {
    setSelectedGroup(group);
    setEditForm({
      name: group.name,
      status: group.status,
      goal_amount: group.goal_amount,
      current_amount: group.current_amount
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    try {
      const params = new URLSearchParams({ token, ...editForm });
      await axios.put(`${API}/admin/groups/${selectedGroup.id}?${params}`);
      toast.success('Group updated successfully');
      setShowEdit(false);
      fetchGroups();
    } catch (error) {
      toast.error('Failed to update group');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm('Are you sure you want to delete this group? All related data will be permanently deleted.')) return;
    try {
      await axios.delete(`${API}/admin/groups/${groupId}?token=${token}`);
      toast.success('Group deleted');
      fetchGroups();
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  const handleExport = async (format) => {
    try {
      toast.loading(`Exporting groups as ${format.toUpperCase()}...`);
      const response = await axios.get(`${API}/admin/export/groups?token=${token}&format=${format}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `groups_export.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.dismiss();
      toast.success(`Groups exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export groups');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search groups..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="pl-10 bg-slate-800 border-slate-700 text-white rounded-xl"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="px-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-xl"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('csv')}
            className="border-slate-700 text-slate-300 hover:text-white"
            data-testid="export-groups-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('xlsx')}
            className="border-slate-700 text-slate-300 hover:text-white"
            data-testid="export-groups-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <span className="text-sm text-slate-400">
            {total} groups total
          </span>
        </div>
      </div>

      {/* Groups Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Group</th>
                <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Creator</th>
                <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Members</th>
                <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Progress</th>
                <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Status</th>
                <th className="text-right px-6 py-4 text-slate-400 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center">
                    <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : groups.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-slate-400">
                    No groups found
                  </td>
                </tr>
              ) : (
                groups.map((group) => {
                  const progress = group.goal_amount > 0 
                    ? Math.min(100, (group.current_amount / group.goal_amount) * 100) 
                    : 0;
                  return (
                    <tr key={group.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                            <Target className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{group.name}</p>
                            <p className="text-xs text-slate-500">₹{group.goal_amount?.toLocaleString()} goal</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-slate-300">{group.creator?.name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500">{group.creator?.phone}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-300">{group.member_ids?.length || 0}/{group.num_members}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-32">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-slate-400">₹{group.current_amount?.toLocaleString()}</span>
                            <span className="text-white">{progress.toFixed(0)}%</span>
                          </div>
                          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          group.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          group.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {group.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => fetchGroupDetail(group.id)}
                            className="text-slate-400 hover:text-white"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(group)}
                            className="text-slate-400 hover:text-white"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteGroup(group.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between">
            <span className="text-sm text-slate-400">
              Page {page + 1} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="border-slate-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="border-slate-700"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Group Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Group Details</DialogTitle>
          </DialogHeader>
          {groupDetail && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold">{groupDetail.group.name}</h3>
                <p className="text-slate-400">{groupDetail.group.description || 'No description'}</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-slate-400 text-sm">Goal</p>
                  <p className="text-xl font-bold text-white">₹{groupDetail.group.goal_amount?.toLocaleString()}</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-slate-400 text-sm">Current</p>
                  <p className="text-xl font-bold text-green-400">₹{groupDetail.group.current_amount?.toLocaleString()}</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-slate-400 text-sm">Members</p>
                  <p className="text-xl font-bold text-blue-400">{groupDetail.members?.length || 0}</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-slate-400 text-sm">Expenses</p>
                  <p className="text-xl font-bold text-orange-400">{groupDetail.expenses?.total || 0}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-2">Contributions</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Verified:</span>
                      <span className="text-green-400">{groupDetail.contributions?.verified || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Pending:</span>
                      <span className="text-yellow-400">{groupDetail.contributions?.pending || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Total Amount:</span>
                      <span className="text-white">₹{groupDetail.contributions?.total_amount?.toLocaleString() || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-2">Expenses</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Count:</span>
                      <span className="text-white">{groupDetail.expenses?.total || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Total Amount:</span>
                      <span className="text-white">₹{groupDetail.expenses?.total_amount?.toLocaleString() || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {groupDetail.members && groupDetail.members.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Members</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {groupDetail.members.map((member) => (
                      <div key={member.id} className="bg-slate-800 rounded-xl p-3 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-sm font-bold">
                          {member.name?.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{member.name}</p>
                          <p className="text-xs text-slate-500">{member.phone}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Edit Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400">Name</label>
              <Input
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="mt-1 bg-slate-800 border-slate-700 text-white rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400">Status</label>
              <select
                value={editForm.status || 'active'}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                className="w-full mt-1 px-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-xl"
              >
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400">Goal Amount</label>
                <Input
                  type="number"
                  value={editForm.goal_amount || 0}
                  onChange={(e) => setEditForm({ ...editForm, goal_amount: parseFloat(e.target.value) })}
                  className="mt-1 bg-slate-800 border-slate-700 text-white rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400">Current Amount</label>
                <Input
                  type="number"
                  value={editForm.current_amount || 0}
                  onChange={(e) => setEditForm({ ...editForm, current_amount: parseFloat(e.target.value) })}
                  className="mt-1 bg-slate-800 border-slate-700 text-white rounded-xl"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={() => setShowEdit(false)} variant="outline" className="flex-1 border-slate-700">
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} className="flex-1 bg-gradient-to-r from-red-500 to-orange-500">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGroups;
